import {useEffect, useMemo} from 'react';
import {useNavigate} from 'react-router-dom';
import {useMutation, useQuery} from '@apollo/client';
import {DatePicker, Form, Input, Select, Tooltip} from 'antd';
import {Button} from '@dabafinance/react-components';
import moment from 'moment';
import {toast} from 'react-toastify';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {TRANSLATE_TEXT} from 'graphql/mutations/notifications';
import {
  CREATE_RECOMMENDATION,
  GET_EXCHANGES,
  UPDATE_RECOMMENDATION,
} from './resolvers';
import {
  INSTRUMENT_KEY,
  LANGUAGE_OPTIONS,
  errorMessage,
  isGqlError,
  languageLabel,
  otherLanguage,
  readMutation,
} from './constants';
import styles from './recommendations.module.scss';

/**
 * Create/edit the parent recommendation.
 *
 * `updateInstrumentRecommendation` is a full input rather than a patch, so both
 * paths submit every field plus both multilingual name rows — that is what keeps
 * the base `name` and the localized records in step.
 */
export const RecommendationFormModal = ({form, record, onClose, onSaved}) => {
  const navigate = useNavigate();
  const isEdit = Boolean(record?.id);
  const baseLanguage = Form.useWatch('language', form);
  const name = Form.useWatch('name', form);

  // Once a source is registered the recommendation has picks, and the backend
  // only accepts an exchange change when every existing pick belongs to the new
  // exchange. Locking it here keeps that failure out of the operator's way.
  const hasPicks = Boolean(record?.availableSources?.length);

  const {data: exchangeData, loading: exchangesLoading} = useQuery(
    GET_EXCHANGES,
    {errorPolicy: 'all'},
  );

  const exchanges = useMemo(
    () =>
      (exchangeData?.getAllStocksExchange || []).filter(
        exchange => !isGqlError(exchange) && exchange?.id,
      ),
    [exchangeData],
  );

  const [createRecommendation, {loading: createLoading}] = useMutation(
    CREATE_RECOMMENDATION,
    {errorPolicy: 'all'},
  );
  const [updateRecommendation, {loading: updateLoading}] = useMutation(
    UPDATE_RECOMMENDATION,
    {errorPolicy: 'all'},
  );
  const [translateText, {loading: translateLoading}] = useMutation(
    TRANSLATE_TEXT,
    {errorPolicy: 'all'},
  );

  useEffect(() => {
    if (!record) return;
    form.setFieldsValue({
      name: record.name,
      language: record.language,
      exchange: record.exchange?.id,
      date: record.date ? moment(record.date) : null,
      translatedName:
        record.multiLang?.find(entry => entry.language !== record.language)
          ?.name || '',
    });
  }, [record, form]);

  const handleTranslateName = () => {
    if (!name || !baseLanguage) return;
    translateText({
      variables: {
        sourceLang: baseLanguage,
        targetLang: otherLanguage(baseLanguage),
        sourceText: name,
      },
    })
      .then(({data}) => {
        if (isGqlError(data?.translateText))
          return toast.error(errorMessage(data?.translateText));
        form.setFieldsValue({translatedName: data.translateText.targetText});
      })
      .catch(error => toast.error(error?.message));
  };

  /** Field-level server errors read better than a toast on the wrong control. */
  const applyServerError = message => {
    if (/already exists/i.test(message)) {
      form.setFields([{name: 'name', errors: [message]}]);
    } else if (/exchange/i.test(message)) {
      form.setFields([{name: 'exchange', errors: [message]}]);
    }
    toast.error(message);
  };

  const handleSubmit = values => {
    const trimmedName = values.name.trim();
    const translatedName = values.translatedName?.trim();
    const input = {
      name: trimmedName,
      date: moment(values.date).toISOString(),
      language: values.language,
      instrumentKey: INSTRUMENT_KEY,
      exchange: values.exchange,
      multiLang: [
        {language: values.language, name: trimmedName},
        ...(translatedName
          ? [{language: otherLanguage(values.language), name: translatedName}]
          : []),
      ],
    };

    const request = isEdit
      ? updateRecommendation({variables: {id: record.id, input}})
      : createRecommendation({variables: {input}});

    request
      .then(result => {
        const {payload, error: failure} = readMutation(
          result,
          isEdit
            ? 'updateInstrumentRecommendation'
            : 'createInstrumentRecommendation',
        );
        if (failure) return applyServerError(failure);

        toast.success(payload.message);
        onSaved?.();
        onClose();

        if (!isEdit && payload.data?.id) {
          const exchange = exchanges.find(item => item.id === values.exchange);
          navigate(`/dashboard/stock-recommendations/${payload.data.id}`, {
            state: {
              recommendation: {
                ...payload.data,
                exchange,
                availableSources: [],
                multiLang: input.multiLang,
              },
            },
          });
        }
      })
      .catch(error => toast.error(error?.message));
  };

  return (
    <div className={styles.modal}>
      <h1>{isEdit ? 'Edit Recommendation' : 'Create Recommendation'}</h1>
      <p className={styles.modalSubtitle}>
        Public Equity picks for a single exchange.
      </p>

      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          label="Original language"
          name="language"
          rules={[{required: true, message: 'Select the original language'}]}>
          <Select options={LANGUAGE_OPTIONS} placeholder="Select a language" />
        </Form.Item>

        <div className={styles.nameField}>
          <Form.Item
            label="Recommendation name"
            name="name"
            className={styles.grow}
            rules={[
              {required: true, message: 'Enter a name'},
              {
                validator: async (rule, value) => {
                  if (value && !value.trim()) throw new Error('Enter a name');
                },
              },
            ]}>
            <Input placeholder="NGX picks — 17 to 21 August 2026" />
          </Form.Item>
          <Button
            label={translateLoading ? 'Translating…' : 'Translate name'}
            type="secondary"
            disabled={!name || !baseLanguage || translateLoading}
            onClick={handleTranslateName}
          />
        </div>

        <Form.Item
          label={`Name in ${languageLabel(
            otherLanguage(baseLanguage || 'EN'),
          )}`}
          name="translatedName"
          tooltip="Recommended. Shown to users reading in the other language.">
          <Input placeholder="Sélection NGX — du 17 au 21 août 2026" />
        </Form.Item>

        <Tooltip
          title={
            hasPicks
              ? 'Exchange is locked once picks exist. Remove the sources and picks first, then change it.'
              : ''
          }>
          <Form.Item
            label="Exchange"
            name="exchange"
            rules={[{required: true, message: 'Select an exchange'}]}>
            <Select
              loading={exchangesLoading}
              disabled={hasPicks}
              placeholder="Select an exchange"
              options={exchanges.map(exchange => ({
                value: exchange.id,
                label: `${exchange.stockExchangeName || exchange.code}${
                  exchange.baseCurrencyCode
                    ? ` · ${exchange.baseCurrencyCode}`
                    : ''
                }`,
              }))}
            />
          </Form.Item>
        </Tooltip>

        <Form.Item
          label="Recommendation date"
          name="date"
          rules={[{required: true, message: 'Select a date'}]}>
          <DatePicker showTime className={styles.fullWidth} />
        </Form.Item>

        <div className={styles.formFooter}>
          <span className={styles.mutedText}>Product: Public Equity</span>
          <SubmitButton
            smallWidth
            disabled={createLoading || updateLoading}
            label={
              createLoading || updateLoading
                ? 'Saving…'
                : isEdit
                ? 'Save changes'
                : 'Create recommendation'
            }
          />
        </div>
      </Form>
    </div>
  );
};

export default RecommendationFormModal;
